import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateArchivedProperties } from "@/lib/mutationHelpers";

export const useArchivedProperties = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.properties.archived, user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // CRITICAL SECURITY: Always filter by owner_id
      // Defense in depth - RLS also enforces this, but we add explicit filter
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("owner_id", user.id)
        .not("archived_at", "is", null)
        .is("deleted_at", null);

      if (error) {
        console.error("[useArchivedProperties] Supabase error:", {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
        });
        console.error("[useArchivedProperties] Full error object:", error);
        throw error;
      }
      
      // Handle empty arrays gracefully
      return data || [];
    },
    enabled: !!user?.id,
  });
};

export const useArchiveProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (propertyId: string) => {
      // Archive: Only set archived_at, do NOT change status
      // archived_at IS NOT NULL = archived state
      const { data, error } = await supabase
        .from("properties")
        .update({ 
          archived_at: new Date().toISOString() 
        })
        .eq("id", propertyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      invalidateArchivedProperties(queryClient);
    },
  });
};

export const useRestoreProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (propertyId: string) => {
      // Unarchive: Only set archived_at = NULL, do NOT change status
      // archived_at IS NULL = active state
      const { data, error } = await supabase
        .from("properties")
        .update({ 
          archived_at: null 
        })
        .eq("id", propertyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      invalidateArchivedProperties(queryClient);
    },
  });
};

export const usePermanentDeleteProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (propertyId: string) => {
      // Delete all related data in order (respecting foreign key constraints)
      // 1. Delete cleaner_purchases
      await supabase.from("cleaner_purchases").delete().eq("property_id", propertyId);
      
      // 2. Delete replenishment_suggestions
      await supabase.from("replenishment_suggestions").delete().eq("property_id", propertyId);
      
      // 3. Delete inventory_submissions
      await supabase.from("inventory_submissions").delete().eq("property_id", propertyId);
      
      // 4. Delete inventory_items
      await supabase.from("inventory_items").delete().eq("property_id", propertyId);
      
      // 5. Delete maintenance_issues
      await supabase.from("maintenance_issues").delete().eq("property_id", propertyId);
      
      // 6. Delete cleaning_submissions
      await supabase.from("cleaning_submissions").delete().eq("property_id", propertyId);
      
      // 7. Delete cleaning_tasks
      await supabase.from("cleaning_tasks").delete().eq("property_id", propertyId);
      
      // 8. Delete reservations
      await supabase.from("reservations").delete().eq("property_id", propertyId);
      
      // 9. Delete checklist_rules
      await supabase.from("checklist_rules").delete().eq("property_id", propertyId);
      
      // 10. Delete checklists
      await supabase.from("checklists").delete().eq("property_id", propertyId);
      
      // 11. Delete task_reference_photos
      await supabase.from("task_reference_photos").delete().eq("property_id", propertyId);
      
      // 12. Delete reference_photos
      await supabase.from("reference_photos").delete().eq("property_id", propertyId);
      
      // 13. Delete cleaner_assignments
      await supabase.from("cleaner_assignments").delete().eq("property_id", propertyId);
      
      // 14. Delete property_cleaning_assignments
      await supabase.from("property_cleaning_assignments").delete().eq("property_id", propertyId);
      
      // 15. Delete message_threads related to property
      await supabase.from("message_threads").delete().eq("property_id", propertyId);
      
      // 16. Delete owner_notifications
      await supabase.from("owner_notifications").delete().eq("property_id", propertyId);
      
      // 17. Delete company_invites
      await supabase.from("company_invites").delete().eq("property_id", propertyId);
      
      // 18. Finally delete the property
      const { error } = await supabase
        .from("properties")
        .delete()
        .eq("id", propertyId);

      if (error) throw error;
    },
    onSuccess: () => {
      invalidateArchivedProperties(queryClient);
    },
  });
};
